import * as React from 'react'
import { css } from '@emotion/react'
import { colors } from 'plume-ui'
import Link, { LinkProps } from 'next/link'

type Theme = 'light-bg' | 'dark-bg'

type Props = {
  theme?: Theme
  children: Array<
    | React.ReactElement<BreadcrumbLinkProps>
    | React.ReactElement<BreadcrumbItemProps>
  >
  className?: string
}

export const Breadcrumb: React.FC<Props> = ({
  theme = 'light-bg',
  ...props
}) => {
  const textColor = theme === 'light-bg' ? colors.text : colors.white00

  return (
    <ol
      itemScope
      itemType="https://schema.org/BreadcrumbList"
      css={css`
        margin: 0;
        padding: 0;
        display: flex;
        align-items: center;
        color: ${textColor};
        font-size: 11px;
        line-height: 1.45;
        li:not(:last-child)::after {
          content: '/';
          margin: 0 5px;
          color: ${theme === 'light-bg' ? colors.gray70 : colors.border};
          font-size: 11px;
          line-height: 1.45;
        }
        a {
          transition: text-decoration 0.2s ease;
          text-decoration: underline solid transparent;
          :hover {
            text-decoration: underline solid ${textColor};
          }
        }
        a:visited,
        a:link {
          color: ${textColor};
        }
      `}
      className={props.className}
    >
      {props.children.map((component, i) => (
        <React.Fragment key={i}>
          <li
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            {component}
            <meta itemProp="position" content={i + 1 + ''} />
          </li>
        </React.Fragment>
      ))}
    </ol>
  )
}

type BreadcrumbLinkProps = LinkProps & {
  children: string
  className?: string
  external?: boolean
}

export const BreadcrumbLink: React.FC<BreadcrumbLinkProps> = (props) => {
  return (
    <Link {...props} passHref>
      <a
        itemProp="item"
        className={props.className}
        target={props.external ? '_blank' : '_top'}
      >
        <span itemProp="name">{props.children}</span>
      </a>
    </Link>
  )
}

type BreadcrumbItemProps = { children: string; className?: string }

export const BreadcrumbItem: React.FC<BreadcrumbItemProps> = (props) => {
  return (
    <span itemProp="name" className={props.className}>
      {props.children}
    </span>
  )
}
